import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { formatPt } from '../../../lib/format.js';
import {
  SupplyRateStackedBar,
  SupplyRateStage,
  SUPPLY_RATE_WARN_THRESHOLD,
} from './SupplyRateParts.js';
import { SupplyRateProjectsModal } from './SupplyRateProjectsModal.js';
import type { PmoSupplyRateStage, PmoSupplyRateResponse } from '../../../types/pmo-flow.js';

export interface SupplyRateCardProps {
  /** 充足率データ（未取得なら undefined） */
  data: PmoSupplyRateResponse | undefined;
  /** 読み込み状態 */
  isLoading: boolean;
}

/** 小数1桁に丸める（合計率をクライアント側で出すため） @graph-connects none */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * 充足率カード。「先 2 週間で消化できる開発量」を分母に、消化される側の在庫が
 * どれだけ積み上がっているかを 1 枚で見せる。
 *
 * 4 つの段は漏斗の関係にある（左 = 上流 → 右 = 下流）:
 *   要件定義アサイン可能 →（要件定義の担当者が決まる）→ 要件定義着手予定
 *   →（要件定義 + 開発準備が完了）→ 開発アサイン可能 →（開発アサイン確定）→ 開発着手予定
 * 上流の段は下流へそのまま移っていくので、別カードに分けず
 * 「要件定義 2 段 ＋ 開発 2 段 ＝ 合計」を横一列に並べ、下の積み上げバーで
 * 同じ関係を面積でも見せる。バーの余白がそのまま「枠に対する不足量」になる。
 *
 * 色はフェーズを色相（要件定義 = ティール / 開発 = ブルー）、段の進みを濃さで表し、
 * 左から右へ濃くなる並びにする。
 *
 * 率だけでは「在庫が薄い」のか「見積が入っていないだけ」のか区別できないため、
 * 見積欠損件数を注記として同じカードに置く。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO充足率カード。要件定義アサイン可能・要件定義着手予定（計上ptは見積平均の15%）・開発アサイン可能・開発着手予定（アサイン済み・未着手）の4段を漏斗として1枚に統合表示し、先2週間分の予算に対する充足率を積み上げバーで示す。各段から案件・バグ明細のドリルダウンを開く（開発アサイン可能段は案件とバグを含む）
 * @graph-connects SupplyRateStage [calls] 段別の率表示
 * @graph-connects SupplyRateStackedBar [calls] 段を積み上げたバー描画
 * @graph-connects SupplyRateProjectsModal [calls] 案件・バグ明細のドリルダウンモーダル表示
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects Badge [calls] 合計充足率バッジ描画
 * @graph-connects formatPt [calls] pt値の整形
 */
export function SupplyRateCard({ data, isLoading }: SupplyRateCardProps) {
  const { t } = useTranslation();
  const [modalStage, setModalStage] = useState<PmoSupplyRateStage | null>(null);

  const requirementAssignableRate = data?.requirementAssignableSupplyRate ?? null;
  const requirementPlannedStartRate = data?.requirementPlannedStartSupplyRate ?? null;
  const assignableRate = data?.supplyRate ?? null;
  const plannedStartRate = data?.plannedStartSupplyRate ?? null;
  const stageRates = [
    requirementAssignableRate,
    requirementPlannedStartRate,
    assignableRate,
    plannedStartRate,
  ];
  const totalRate = stageRates.every((rate) => rate !== null)
    ? round1(stageRates.reduce((sum, rate) => sum + (rate ?? 0), 0))
    : null;
  const isShort = totalRate !== null && totalRate < SUPPLY_RATE_WARN_THRESHOLD;

  const missingBudgetMonths = (data?.denominator.segments ?? [])
    .filter((segment) => segment.monthlyBudgetPoints === null)
    .map((segment) => segment.month);
  // 開発着手予定と要件定義 2 段は見積必須で抽出しているため欠損が無い。
  // 見積未入力で 0pt 計上になりうるのは開発アサイン可能段だけ
  const missingEstimateCount = data?.numerator.missingEstimateCount ?? 0;
  const requirementPointPercent = Math.round((data?.requirementPointRatio ?? 0) * 100);

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={t('kpi.pmoFlow.supplyRate.title')}
        description={t('kpi.pmoFlow.supplyRate.description')}
        action={
          totalRate !== null ? (
            <Badge variant={isShort ? 'warning' : 'success'} size="md">
              {t('kpi.pmoFlow.supplyRate.badge', { value: totalRate })}
            </Badge>
          ) : undefined
        }
      />
      <CardContent>
        {isLoading ? (
          <div className="h-32 animate-pulse rounded-lg bg-[var(--color-bg-elevated)]" />
        ) : data ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-5 sm:gap-2">
              <SupplyRateStage
                label={t('kpi.pmoFlow.supplyRate.stage.requirementAssignable')}
                rate={requirementAssignableRate}
                dotColor="--color-supply-req-assignable"
                hint={t('kpi.pmoFlow.supplyRate.stage.requirementAssignableHint', {
                  count: data.requirementAssignable.projectCount,
                  pt: formatPt(data.requirementAssignable.points),
                  percent: requirementPointPercent,
                })}
                onClick={() => {
                  setModalStage('requirementAssignable');
                }}
              />
              <SupplyRateStage
                label={t('kpi.pmoFlow.supplyRate.stage.requirementPlannedStart')}
                rate={requirementPlannedStartRate}
                operator="+"
                dotColor="--color-supply-req-planned"
                hint={t('kpi.pmoFlow.supplyRate.stage.requirementPlannedStartHint', {
                  count: data.requirementPlannedStart.projectCount,
                  pt: formatPt(data.requirementPlannedStart.points),
                  percent: requirementPointPercent,
                })}
                onClick={() => {
                  setModalStage('requirementPlannedStart');
                }}
              />
              <SupplyRateStage
                label={t('kpi.pmoFlow.supplyRate.stage.assignable')}
                rate={assignableRate}
                operator="+"
                dotColor="--color-supply-dev-assignable"
                hint={t('kpi.pmoFlow.supplyRate.stage.assignableHint', {
                  count: data.numerator.readyProjectCount,
                  projectPt: formatPt(data.numerator.readyProjectPoints),
                  lightCount: data.numerator.lightTaskCount,
                  lightPt: formatPt(data.numerator.lightTaskPoints),
                })}
                onClick={() => {
                  setModalStage('assignable');
                }}
              />
              <SupplyRateStage
                label={t('kpi.pmoFlow.supplyRate.stage.plannedStart')}
                rate={plannedStartRate}
                operator="+"
                dotColor="--color-supply-dev-planned"
                hint={t('kpi.pmoFlow.supplyRate.stage.plannedStartHint', {
                  count: data.plannedStart.projectCount,
                  pt: formatPt(data.plannedStart.points),
                })}
                onClick={() => {
                  setModalStage('plannedStart');
                }}
              />
              <SupplyRateStage
                label={t('kpi.pmoFlow.supplyRate.stage.total')}
                rate={totalRate}
                operator="="
                hint={t('kpi.pmoFlow.supplyRate.stage.totalHint', {
                  numerator: formatPt(
                    data.requirementAssignable.points +
                      data.requirementPlannedStart.points +
                      data.numerator.totalPoints +
                      data.plannedStart.points,
                  ),
                  denominator:
                    data.denominator.points !== null ? formatPt(data.denominator.points) : '-',
                  weeks: data.denominator.weeks,
                })}
              />
            </div>

            <div className="mt-4">
              <SupplyRateStackedBar
                requirementAssignablePercent={requirementAssignableRate ?? 0}
                requirementPlannedStartPercent={requirementPlannedStartRate ?? 0}
                assignablePercent={assignableRate ?? 0}
                plannedStartPercent={plannedStartRate ?? 0}
                label={t('kpi.pmoFlow.supplyRate.barLabel', {
                  requirementAssignable: requirementAssignableRate ?? 0,
                  requirementPlannedStart: requirementPlannedStartRate ?? 0,
                  assignable: assignableRate ?? 0,
                  plannedStart: plannedStartRate ?? 0,
                })}
              />
            </div>

            <div className="mt-3 space-y-1 text-xs text-[var(--color-text-muted)]">
              {missingBudgetMonths.length > 0 && (
                <p>
                  {t('kpi.pmoFlow.supplyRate.denominatorSegmentMissing', {
                    months: missingBudgetMonths.join(' / '),
                  })}
                </p>
              )}
              {missingEstimateCount > 0 && (
                <p>
                  {t('kpi.pmoFlow.supplyRate.missingEstimateNote', {
                    count: missingEstimateCount,
                  })}
                </p>
              )}
              <p>{t('kpi.pmoFlow.supplyRate.scopeNote', { days: data.recentDays })}</p>
            </div>

            <SupplyRateProjectsModal
              open={modalStage !== null}
              onClose={() => {
                setModalStage(null);
              }}
              stage={modalStage ?? 'assignable'}
            />
          </>
        ) : (
          <div className="flex items-center justify-center rounded-lg border border-dashed border-[var(--color-border)] py-8">
            <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
